/**
 * 교회 제출 화면 (/report)
 *  1) 교회 등록번호 → 등록교회 확인 → 담당자 이름·휴대폰으로 접속
 *  2) 보고서 올리기: 교사별 클럽 선택 + 바로 촬영 / 파일 선택 (+ 수료일 요청)
 *  3) 제출 현황: 본부 처리 단계(제출→본부 접수→확인 중→발급/반려→수령), 인쇄·PDF
 *  4) 교사 발급 현황: 명단 보기 + 신규 교사 등록
 *  - 수령까지 끝난 건은 '수령 완료'로 접히고, 확인 후 목록에서 지울 수 있음. 본부가 삭제한 건은 보이지 않음
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Container, CssBaseline, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Tab, Tabs, TextField, ThemeProvider, Typography,
} from '@mui/material';
import { Close, Logout } from '@mui/icons-material';
import { api, CertData, ClubCode, ReportStatus } from '../api';
import { C, CLUBS, YELLOW_GRADIENT, ON_YELLOW, certDate, clubLabel, formatPhoneInput, mdhm, todayYmd, yellowTheme, toEnName, isValidEnName } from '../common';
import { downloadCertificatesPdf } from '../certificate/Certificate';

type Church = { regNo: string; churchName: string; location: string; region: string; teacherCount?: number; managerName?: string; managerPhone?: string };
type Teacher = { _id: string; teacherName: string; teacherNameEn: string; phoneMasked: string; clubs: string[]; position: string; awanaRole: string; keyCode: string; education: Array<{ courseCode: string; year: number; result: string }>; certificates: CertData[] };
type Report = {
  _id: string; teacherName: string; club: ClubCode; clubLabel: string; status: ReportStatus; submittedAt: string; receivedAt: string | null;
  checkingAt: string | null; decidedAt: string | null; rejectReason: string; requestedDate: string; fileCount: number; certificate: CertData | null;
};
type UploadRow = { key: number; teacherId: string; teacherName: string; teacherNameEn: string; club: ClubCode | ''; files: File[]; previews: string[]; wantDate: boolean; date: string; reason: string };

let rowKey = 1;
const newRow = (patch: Partial<UploadRow> = {}): UploadRow => ({ key: rowKey++, teacherId: '', teacherName: '', teacherNameEn: '', club: '', files: [], previews: [], wantDate: false, date: '', reason: '', ...patch });

/** 큰 사진은 2400px · JPEG 로 줄여서 올림 (HEIC 등 변환 불가 시 원본) */
const shrinkImage = (file: File) => new Promise<File>((resolve) => {
  if (!file.type.startsWith('image/') || file.size < 1.5 * 1024 * 1024) return resolve(file);
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 2400 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      URL.revokeObjectURL(url);
      resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file);
    }, 'image/jpeg', 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const ChurchReportPortal: React.FC = () => {
  const [church, setChurch] = useState<Church | null>(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState(0);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [rows, setRows] = useState<UploadRow[]>([newRow()]);

  useEffect(() => {
    api<{ data: Church }>('/church-portal/me').then((r) => setChurch(r.data)).catch(() => setChurch(null)).finally(() => setChecking(false));
  }, []);
  const loadTeachers = () => api<{ data: Teacher[] }>('/church-portal/teachers').then((r) => { setTeachers(r.data); return r.data; }).catch(() => [] as Teacher[]);
  useEffect(() => { if (church) loadTeachers(); }, [church]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => { await api('/church-portal/logout', { body: {} }).catch(() => undefined); setChurch(null); setTab(0); };
  const reupload = (r: Report) => {
    const t = teachers.find((x) => x.teacherName === r.teacherName);
    setRows([newRow({ teacherId: t?._id || '', teacherName: r.teacherName, teacherNameEn: t?.teacherNameEn || '', club: r.club })]);
    setTab(0);
  };

  return (
    <ThemeProvider theme={yellowTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100dvh', bgcolor: C.bg }}>
        <Box sx={{ background: YELLOW_GRADIENT, color: ON_YELLOW }}>
          <Container maxWidth="md" sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="img" src="/logo.png" alt="" sx={{ height: 22, display: { xs: 'none', sm: 'block' } }} onError={(e: any) => { e.target.style.display = 'none'; }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={800} fontSize={18}>BT 완성보고서 제출</Typography>
              {church && <Typography fontSize={13} sx={{ opacity: 0.85 }}>{church.churchName} · 등록번호 {church.regNo} · {church.managerName}</Typography>}
            </Box>
            {church && <IconButton color="inherit" onClick={logout} aria-label="나가기"><Logout /></IconButton>}
          </Container>
          {church && (
            <Container maxWidth="md">
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
                sx={{ '& .MuiTab-root': { color: ON_YELLOW, opacity: 0.75 }, '& .Mui-selected': { opacity: 1, color: `${ON_YELLOW} !important` }, '& .MuiTabs-indicator': { bgcolor: ON_YELLOW, height: 3 } }}>
                <Tab label="보고서 올리기" /><Tab label="제출 현황" /><Tab label="교사 발급 현황" />
              </Tabs>
            </Container>
          )}
        </Box>
        <Container maxWidth="md" sx={{ py: 3 }}>
          {checking ? <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
            : !church ? <ChurchLogin onLogin={setChurch} />
              : tab === 0 ? <UploadPanel teachers={teachers} rows={rows} setRows={setRows} onDone={() => setTab(1)} reloadTeachers={loadTeachers} />
                : tab === 1 ? <StatusPanel onReupload={reupload} />
                  : <TeachersPanel teachers={teachers} reloadTeachers={loadTeachers} />}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

// ───────── 1. 접속 ─────────
const ChurchLogin: React.FC<{ onLogin: (c: Church) => void }> = ({ onLogin }) => {
  const [regNo, setRegNo] = useState('');
  const [info, setInfo] = useState<Church | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setInfo(null); setError('');
    if (regNo.length !== 4) return;
    api<{ data: Church }>(`/church-portal/lookup?regNo=${regNo}`).then((r) => setInfo(r.data)).catch((e) => setError(e.message));
  }, [regNo]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await api<{ data: Church }>('/church-portal/login', { body: { regNo, managerName: name, managerPhone: phone } });
      onLogin(r.data);
    } catch (err: any) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Paper component="form" onSubmit={submit} variant="outlined" sx={{ maxWidth: 440, mx: 'auto', p: { xs: 2.5, sm: 4 }, borderRadius: 4, borderColor: C.line }}>
      <Typography fontSize={20} fontWeight={800} mb={2.5}>교회 등록번호로 접속</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TextField label="교회 등록번호 (4자리)" fullWidth value={regNo} autoFocus inputProps={{ inputMode: 'numeric', maxLength: 4 }}
        onChange={(e) => setRegNo(e.target.value.replace(/\D/g, '').slice(0, 4))} sx={{ mb: 2 }} />
      {info && (
        <Box sx={{ p: 1.75, mb: 2, borderRadius: 2.5, bgcolor: '#fff8d6', border: '1px solid #f3dc7c' }}>
          <Typography fontSize={12} fontWeight={800} color="#8a6d00">등록교회</Typography>
          <Typography fontSize={18} fontWeight={800}>{info.churchName}</Typography>
          <Typography fontSize={13} color={C.sub}>{[info.region, info.location].filter(Boolean).join(' · ')} {info.teacherCount ? `· 등록 교사 ${info.teacherCount}명` : ''}</Typography>
        </Box>
      )}
      <TextField label="담당자 이름" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} disabled={!info} />
      <TextField label="담당자 휴대폰" fullWidth value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value))} placeholder="010-0000-0000"
        inputProps={{ inputMode: 'numeric' }} sx={{ mb: 1 }} disabled={!info} helperText="BT 신청 때 입력한 담당자 또는 우리 교회 교사의 휴대폰 번호" />
      <Button type="submit" variant="contained" size="large" fullWidth disabled={!info || !name.trim() || phone.replace(/\D/g, '').length < 10 || busy} sx={{ mt: 2, minHeight: 50 }}>
        {busy ? '확인 중…' : '접속하기'}
      </Button>
      <Typography fontSize={12} color={C.sub} mt={2}>사진으로 올리기 어려우면 report@awana.or.kr 로 보내주셔도 됩니다.</Typography>
    </Paper>
  );
};

// ───────── 2. 보고서 올리기 ─────────
const UploadPanel: React.FC<{ teachers: Teacher[]; rows: UploadRow[]; setRows: React.Dispatch<React.SetStateAction<UploadRow[]>>; onDone: () => void; reloadTeachers: () => Promise<Teacher[]> }> = ({ teachers, rows, setRows, onDone, reloadTeachers }) => {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [showIssued, setShowIssued] = useState(false);
  const [newFor, setNewFor] = useState<number | null>(null);
  // 수료증이 이미 발급된 교사는 선택 목록에서 자동으로 빠짐 (다른 클럽 수료증이 필요하면 '발급된 교사도 보기')
  const issuedCount = teachers.filter((t) => t.certificates.length).length;
  const options = useMemo(() => teachers
    .filter((t) => showIssued || !t.certificates.length)
    .map((t) => ({ id: t._id, label: t.teacherName, en: t.teacherNameEn, sub: `${t.phoneMasked} ${t.clubs.join('·')}${t.certificates.length ? ` · 발급: ${t.certificates.map((c) => c.clubLabel).join(', ')}` : ''}`, clubs: t.clubs })), [teachers, showIssued]);
  const patch = (key: number, p: Partial<UploadRow>) => setRows((current) => current.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const setFiles = async (row: UploadRow, list: FileList | null) => {
    if (!list?.length) return;
    const files = await Promise.all(Array.from(list).slice(0, 4).map(shrinkImage));
    row.previews.forEach((u) => URL.revokeObjectURL(u));
    patch(row.key, { files, previews: files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : '')) });
  };
  const valid = rows.length > 0 && rows.every((r) => r.teacherName.trim() && isValidEnName(r.teacherNameEn) && r.club && r.files.length && (!r.wantDate || (r.date && r.reason.trim())));
  const submit = async () => {
    setBusy(true); setError('');
    const form = new FormData();
    const items = rows.map((r, i) => ({
      teacherId: r.teacherId || undefined, teacherName: r.teacherName.trim(), teacherNameEn: r.teacherNameEn.trim().toUpperCase(), club: r.club, fileField: `file_${i}`,
      requestedDate: r.wantDate ? r.date : undefined, requestedDateReason: r.wantDate ? r.reason : undefined,
    }));
    rows.forEach((r, i) => r.files.forEach((f) => form.append(`file_${i}`, f, f.name)));
    form.append('items', JSON.stringify(items));
    try {
      const r = await api<{ data: { count: number } }>('/church-portal/reports', { form });
      setDone(r.data.count);
      rows.forEach((x) => x.previews.forEach((u) => URL.revokeObjectURL(u)));
      setRows([newRow()]);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 4, borderColor: C.line }}>
        <Typography fontSize={44}>✅</Typography>
        <Typography fontSize={20} fontWeight={800}>{done}장 제출했습니다</Typography>
        <Typography color={C.sub} mt={1}>본부에서 확인하면 ‘제출 현황’에 단계가 표시되고, 발급되면 바로 인쇄·PDF로 받을 수 있습니다.</Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 3 }}>
          <Button variant="outlined" onClick={() => setDone(0)}>더 올리기</Button>
          <Button variant="contained" onClick={onDone}>제출 현황 보기</Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Alert severity="info" icon={false} sx={{ bgcolor: '#fff8d6', color: ON_YELLOW, borderRadius: 3 }}>
        교사 <b>1명 = 보고서 1장</b>입니다. 보고서 전체가 잘 보이도록 밝은 곳에서 위에서 찍어주세요. 2단계 점수와 서명이 빠지지 않았는지 확인해주세요.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {issuedCount > 0 && (
        <FormControlLabel control={<Checkbox size="small" checked={showIssued} onChange={(e) => setShowIssued(e.target.checked)} />}
          label={<Typography fontSize={13} color={C.sub}>수료증 발급이 끝난 교사 {issuedCount}명은 선택 목록에서 뺐습니다 — 다른 클럽 수료증이 필요하면 체크</Typography>} />
      )}
      {rows.map((r, i) => (
        <Paper key={r.key} variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: C.line }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography fontWeight={800} sx={{ flex: 1 }}>교사 {i + 1}</Typography>
            {rows.length > 1 && <IconButton size="small" onClick={() => setRows(rows.filter((x) => x.key !== r.key))}><Close fontSize="small" /></IconButton>}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr' }, gap: 1.5 }}>
            <Autocomplete freeSolo options={options} getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
              inputValue={r.teacherName}
              onInputChange={(_, v, reason) => { if (reason === 'input' || reason === 'clear') patch(r.key, { teacherName: v, teacherId: '', teacherNameEn: r.teacherId ? '' : r.teacherNameEn }); }}
              onChange={(_, v) => {
                if (v && typeof v !== 'string') {
                  const club = (v.clubs.length === 1 ? v.clubs[0] : r.club) as ClubCode | '';
                  patch(r.key, { teacherId: v.id, teacherName: v.label, teacherNameEn: v.en || r.teacherNameEn, club });
                }
              }}
              renderOption={(props, o) => <li {...props} key={o.id}><Box><Typography fontWeight={700}>{o.label}</Typography><Typography fontSize={12} color={C.sub}>{o.sub}</Typography></Box></li>}
              renderInput={(p) => <TextField {...p} label="교사 이름" helperText={r.teacherId ? '등록된 교사' : r.teacherName ? '명단에 없는 이름 — 아래 ‘신규 교사 등록’을 권합니다' : ''} />} />
            <TextField select required label="클럽" value={r.club} onChange={(e) => patch(r.key, { club: e.target.value as ClubCode })}>
              {CLUBS.map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
            </TextField>
          </Box>
          <Button size="small" sx={{ mt: 0.5 }} onClick={() => setNewFor(r.key)}>＋ 명단에 없으면 신규 교사 등록</Button>
          <TextField fullWidth required label="영문 이름" value={r.teacherNameEn} sx={{ mt: 1 }} placeholder="HONG GILDONG"
            inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false }}
            error={Boolean(r.teacherName) && !isValidEnName(r.teacherNameEn)}
            onChange={(e) => patch(r.key, { teacherNameEn: toEnName(e.target.value) })}
            helperText={isValidEnName(r.teacherNameEn) ? '수료증에 이 영문 이름이 들어갑니다.' : '수료증 발급에 반드시 필요합니다. 여권 영문 이름처럼 영어로 입력해주세요. (예: HONG GILDONG)'} />
          {!r.files.length ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5 }}>
              <Button component="label" variant="contained" size="large" sx={{ minHeight: 64, fontSize: 16 }}>
                📷 바로 촬영
                <input hidden type="file" accept="image/*" capture="environment" onChange={(e) => setFiles(r, e.target.files)} />
              </Button>
              <Button component="label" variant="outlined" size="large" sx={{ minHeight: 64, fontSize: 16 }}>
                📁 파일 선택
                <input hidden type="file" multiple accept="image/*,application/pdf" onChange={(e) => setFiles(r, e.target.files)} />
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {r.files.map((f, j) => (
                  <Box key={j} sx={{ width: 120, height: 150, borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.line}`, display: 'grid', placeItems: 'center', bgcolor: '#f7f3e3' }}>
                    {r.previews[j] ? <img src={r.previews[j]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Typography fontSize={12} fontWeight={800} color={C.sub}>{f.name.slice(-18)}</Typography>}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button component="label" size="small" variant="outlined">다시 촬영<input hidden type="file" accept="image/*" capture="environment" onChange={(e) => setFiles(r, e.target.files)} /></Button>
                <Button component="label" size="small">파일 변경<input hidden type="file" multiple accept="image/*,application/pdf" onChange={(e) => setFiles(r, e.target.files)} /></Button>
              </Box>
            </Box>
          )}
          <FormControlLabel sx={{ mt: 1 }} control={<Checkbox size="small" checked={r.wantDate} onChange={(e) => patch(r.key, { wantDate: e.target.checked })} />}
            label={<Typography fontSize={13.5}>수료증 날짜를 특정 날짜로 요청 (선택)</Typography>} />
          {r.wantDate && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField size="small" type="date" value={r.date} inputProps={{ max: todayYmd() }} onChange={(e) => patch(r.key, { date: e.target.value })} />
              <TextField size="small" placeholder="요청 사유 (예: 교회 수료식 날짜)" value={r.reason} onChange={(e) => patch(r.key, { reason: e.target.value })} sx={{ flex: 1, minWidth: 200 }} />
            </Box>
          )}
          <Typography fontSize={12} color={C.sub} mt={0.5}>날짜를 요청하지 않으면 수료증 날짜는 본부 승인일입니다.</Typography>
        </Paper>
      ))}
      <Button variant="outlined" onClick={() => setRows([...rows, newRow()])}>＋ 교사 추가</Button>
      <NewTeacherDialog open={newFor !== null} onClose={() => setNewFor(null)} initialName={rows.find((x) => x.key === newFor)?.teacherName || ''}
        onCreated={async (t) => {
          const list = await reloadTeachers();
          const created = list.find((x) => x._id === t._id);
          if (newFor !== null) {
            patch(newFor, { teacherId: t._id, teacherName: t.teacherName, teacherNameEn: created?.teacherNameEn || '', club: (created?.clubs.length === 1 ? created.clubs[0] : '') as ClubCode | '' });
          }
          setNewFor(null);
        }} />
      <Button variant="contained" size="large" disabled={!valid || busy} onClick={submit} sx={{ minHeight: 54, fontSize: 17 }}>
        {busy ? '올리는 중…' : `${rows.length}장 제출하기`}
      </Button>
    </Box>
  );
};

// ───────── 3. 제출 현황 ─────────
const STEPS = ['제출', '본부 접수', '확인 중', '발급', '수령'];

const Tracker: React.FC<{ r: Report }> = ({ r }) => {
  const rc = r.certificate?.churchReceipt;
  const received = rc && rc.pdf + rc.print > 0;
  const rejected = r.status === 'rejected';
  const times = [r.submittedAt, r.receivedAt, r.checkingAt, r.decidedAt, received ? rc!.lastAt : null];
  const reached = [true, Boolean(r.receivedAt), Boolean(r.checkingAt), r.status === 'approved' || rejected, Boolean(received)];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', mt: 1.5 }}>
      {STEPS.map((s, i) => {
        const on = reached[i];
        const isRej = rejected && i === 3;
        const color = isRej ? C.err : on ? '#d99a00' : '#d9d3bd';
        return (
          <Box key={s} sx={{ textAlign: 'center', position: 'relative' }}>
            {i > 0 && <Box sx={{ position: 'absolute', top: 10, right: '50%', width: '100%', height: 3, bgcolor: on ? '#f5c400' : '#ece6cf', zIndex: 0 }} />}
            <Box sx={{ position: 'relative', zIndex: 1, mx: 'auto', width: 22, height: 22, borderRadius: '50%', bgcolor: on ? color : '#fff', border: `3px solid ${color}`, color: '#fff', fontSize: 12, fontWeight: 800, display: 'grid', placeItems: 'center' }}>
              {on ? (isRej ? '✕' : '✓') : ''}
            </Box>
            <Typography fontSize={12} fontWeight={on ? 800 : 500} color={isRej ? C.err : on ? C.ink : C.sub} mt={0.5}>{isRej ? '반려' : s}</Typography>
            <Typography fontSize={10.5} color={C.sub}>{on && times[i] ? mdhm(times[i]) : ''}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const StatusPanel: React.FC<{ onReupload: (r: Report) => void }> = ({ onReupload }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<{ summary: { waiting: number; checking: number; issued: number; rejected: number; done: number }; reports: Report[]; done: Report[] } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [showDone, setShowDone] = useState(false);
  const dismiss = async (ids: string[], label: string) => {
    if (!window.confirm(`${label} 목록에서 지울까요? 수료증은 ‘교사 발급 현황’에서 계속 볼 수 있습니다.`)) return;
    try { await api('/church-portal/reports/dismiss', { body: { ids } }); load(); } catch (e: any) { setError(e.message); }
  };
  const load = () => api<{ data: any }>('/church-portal/reports').then((r) => setData(r.data)).catch((e) => setError(e.message));
  useEffect(() => { load(); const t = window.setInterval(load, 60000); return () => window.clearInterval(t); }, []);
  const pdf = async (certs: CertData[]) => {
    setBusy(certs.length > 1 ? 'all' : certs[0]._id);
    try {
      await downloadCertificatesPdf(certs, certs.length > 1 ? `BT수료증_${certs.length}장.pdf` : undefined);
      await api('/church-portal/certificates/log', { body: { ids: certs.map((c) => c._id), type: 'pdf' } });
      load();
    } catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  const issued = data.reports.filter((r) => r.status === 'approved' && r.certificate).map((r) => r.certificate!);
  const s = data.summary;
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: C.line }}>
        <Typography fontWeight={800} mb={1}>본부 처리 현황</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, textAlign: 'center' }}>
          {[['접수 대기', s.waiting, C.warn], ['확인 중', s.checking, C.info], ['발급', s.issued, C.ok], ['반려', s.rejected, C.err]].map(([l, n, col]) => (
            <Box key={l as string} sx={{ py: 1, borderRadius: 2, bgcolor: '#fffdf4', border: `1px solid ${C.line}` }}>
              <Typography fontSize={24} fontWeight={800} color={Number(n) ? (col as string) : C.sub}>{n as number}</Typography>
              <Typography fontSize={12.5} color={C.sub}>{l as string}</Typography>
            </Box>
          ))}
        </Box>
        {issued.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate(`/report/print?ids=${issued.map((c) => c._id).join(',')}`)}>🖨 모두 인쇄</Button>
            <Button variant="contained" disabled={busy === 'all'} onClick={() => pdf(issued)}>{busy === 'all' ? '만드는 중…' : `⬇ 모두 PDF (${issued.length}장)`}</Button>
          </Box>
        )}
      </Paper>
      {!data.reports.length && <Typography color={C.sub} textAlign="center" py={4}>{(data.done || []).length ? '진행 중인 보고서가 없습니다.' : '아직 제출한 보고서가 없습니다.'}</Typography>}
      {data.reports.map((r) => {
        const c = r.certificate;
        const got = c?.churchReceipt && c.churchReceipt.pdf + c.churchReceipt.print > 0;
        return (
          <Paper key={r._id} variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: r.status === 'rejected' ? '#f5b4b4' : C.line }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography fontWeight={800} fontSize={17}>{r.teacherName}</Typography>
              <Chip size="small" label={clubLabel(r.club)} sx={{ height: 22, bgcolor: '#fff6cc', color: '#6b5200' }} />
              {c && <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: C.ok }}>No. {c.serial}</Typography>}
              <Box flex={1} />
              {got && <Chip size="small" label="수령 ✓" sx={{ height: 22, bgcolor: C.okBg, color: C.ok }} />}
            </Box>
            <Tracker r={r} />
            {r.status === 'rejected' && (
              <Alert severity="error" sx={{ mt: 1.5 }} action={<Box sx={{ display: 'grid' }}><Button color="inherit" size="small" onClick={() => onReupload(r)}>다시 올리기</Button><Button color="inherit" size="small" onClick={() => dismiss([r._id], '이 반려 건을')}>지우기</Button></Box>}>
                반려 사유: {r.rejectReason}
              </Alert>
            )}
            {c && (
              <Box sx={{ mt: 1.5 }}>
                {c.needsReReceive && <Alert severity="warning" sx={{ mb: 1 }}>날짜 변경됨 – 다시 받아주세요 (수료일 {certDate(c.completedAt)})</Alert>}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography fontSize={13} color={C.sub} sx={{ flex: 1 }}>수료일 {certDate(c.completedAt)}{got ? ` · 최근 수령 ${mdhm(c.churchReceipt!.lastAt)}` : ''}</Typography>
                  <Button variant="outlined" onClick={() => navigate(`/report/print?ids=${c._id}`)}>🖨 인쇄</Button>
                  <Button variant="contained" disabled={busy === c._id} onClick={() => pdf([c])}>{busy === c._id ? '만드는 중…' : '⬇ PDF'}</Button>
                </Box>
              </Box>
            )}
            {r.requestedDate && r.status !== 'approved' && <Typography fontSize={12.5} color={C.warn} mt={1}>수료일 요청: {r.requestedDate}</Typography>}
          </Paper>
        );
      })}
      {(data.done || []).length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: C.line, overflow: 'hidden' }}>
          <Box onClick={() => setShowDone(!showDone)} sx={{ p: 1.75, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', bgcolor: '#fffbea' }}>
            <Typography fontWeight={800} sx={{ flex: 1 }}>수령 완료 {data.done.length}건 {showDone ? '▲' : '▼'}</Typography>
            <Button size="small" onClick={(e) => { e.stopPropagation(); dismiss(data.done.map((d) => d._id), `수령 완료 ${data.done.length}건을 모두`); }}>모두 확인 · 지우기</Button>
          </Box>
          <Collapse in={showDone}>
            {data.done.map((r) => (
              <Box key={r._id} sx={{ px: 2, py: 1.25, borderTop: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography fontWeight={800}>{r.teacherName}</Typography>
                <Chip size="small" label={clubLabel(r.club)} sx={{ height: 20, bgcolor: '#fff6cc', color: '#6b5200' }} />
                <Box flex={1} />
                <Button size="small" onClick={() => navigate(`/report/print?ids=${r.certificate?._id}`)}>다시 받기</Button>
                <Button size="small" variant="outlined" onClick={() => dismiss([r._id], `${r.teacherName} 선생님 건을`)}>확인 · 지우기</Button>
                <Typography fontSize={12.5} color={C.sub} sx={{ flexBasis: '100%' }}>No. {r.certificate?.serial} · 수령 {mdhm(r.certificate?.churchReceipt?.lastAt)}</Typography>
              </Box>
            ))}
          </Collapse>
        </Paper>
      )}
      <Typography fontSize={12} color={C.sub} textAlign="center">수령까지 끝난 건은 ‘수령 완료’로 옮겨집니다. 담임목사 서명란은 인쇄 후 직접 서명해주세요.</Typography>
    </Box>
  );
};

// ───────── 4. 교사 발급 현황 ─────────
const TeachersPanel: React.FC<{ teachers: Teacher[]; reloadTeachers: () => Promise<Teacher[]> }> = ({ teachers, reloadTeachers }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const list = teachers.filter((t) => !q || t.teacherName.includes(q));
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField size="small" placeholder="이름 검색" value={q} onChange={(e) => setQ(e.target.value)} sx={{ flex: 1 }} />
        <Button variant="contained" onClick={() => setAdding(true)}>＋ 신규 교사 등록</Button>
      </Box>
      <Typography fontSize={13} color={C.sub}>교사 {teachers.length}명 · BT 교육 때 등록하지 않은 교사는 ‘신규 교사 등록’으로 추가하세요. 정보 수정은 본부(report@awana.or.kr)로 알려주세요.</Typography>
      <NewTeacherDialog open={adding} onClose={() => setAdding(false)} onCreated={async (t) => { await reloadTeachers(); setAdding(false); setOpen(t._id); }} />
      {list.map((t) => (
        <Paper key={t._id} variant="outlined" sx={{ borderRadius: 3, borderColor: C.line, overflow: 'hidden' }}>
          <Box onClick={() => setOpen(open === t._id ? '' : t._id)} sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', cursor: 'pointer' }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={800}>{t.teacherName} <span style={{ fontSize: 12, color: C.sub, fontWeight: 500 }}>{t.teacherNameEn}</span></Typography>
              <Typography fontSize={12.5} color={C.sub}>{t.clubs.join(' · ') || '클럽 미지정'} {t.position && `· ${t.position}`}</Typography>
            </Box>
            {t.certificates.length ? <Chip size="small" label="BT 수료" sx={{ bgcolor: C.okBg, color: C.ok }} /> : <Chip size="small" label="미수료" sx={{ bgcolor: 'transparent', border: `1px dashed ${C.gray}`, color: C.gray }} />}
          </Box>
          {open === t._id && (
            <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${C.line}`, pt: 1.5, display: 'grid', gap: 0.75 }}>
              <Typography fontSize={13}>휴대폰 {t.phoneMasked} · 교사번호 {t.keyCode}</Typography>
              <Typography fontSize={13}>직분·역할: {[t.position, t.awanaRole].filter(Boolean).join(' · ') || '-'}</Typography>
              <Typography fontSize={13}>교육 이수: {t.education.length ? t.education.map((e) => `${e.year} ${e.courseCode}${e.result === 'completed' ? '' : '(출석)'}`).join(', ') : '-'}</Typography>
              {t.certificates.map((c) => (
                <Box key={c._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography fontSize={13} sx={{ flex: 1 }}>수료증 <b>{c.serial}</b> · {c.clubLabel} · {certDate(c.completedAt)}</Typography>
                  <Button size="small" onClick={() => navigate(`/report/print?ids=${c._id}`)}>인쇄 / PDF</Button>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );
};

// ───────── 신규 교사 등록 ─────────
const NewTeacherDialog: React.FC<{ open: boolean; onClose: () => void; initialName?: string; onCreated: (t: { _id: string; teacherName: string }) => void }> = ({ open, onClose, initialName, onCreated }) => {
  const [form, setForm] = useState({ teacherName: '', teacherNameEn: '', teacherPhone: '', clubs: [] as string[], position: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm({ teacherName: initialName || '', teacherNameEn: '', teacherPhone: '', clubs: [], position: '' }); setError(''); } }, [open, initialName]);
  const submit = async () => {
    setBusy(true); setError('');
    try {
      const r = await api<{ data: { _id: string; teacherName: string; existed: boolean } }>('/church-portal/teachers', { body: form });
      if (r.data.existed) window.alert('이미 등록된 교사입니다. 기존 정보로 선택했습니다.');
      onCreated(r.data);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  const valid = form.teacherName.trim() && isValidEnName(form.teacherNameEn) && form.teacherPhone.replace(/\D/g, '').length >= 10 && form.clubs.length;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle fontWeight={800}>신규 교사 등록</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.75, pt: '8px !important' }}>
        <Typography fontSize={13} color={C.sub}>BT 교육 때 등록하지 않은 교사를 우리 교회 명단에 추가합니다.</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="이름*" value={form.teacherName} onChange={(e) => setForm({ ...form, teacherName: e.target.value })} />
        <TextField label="영문 이름" required value={form.teacherNameEn} placeholder="HONG GILDONG" inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false }}
          onChange={(e) => setForm({ ...form, teacherNameEn: toEnName(e.target.value) })} helperText="수료증 발급에 반드시 필요합니다. 영어로 입력해주세요." />
        <TextField label="휴대폰*" value={form.teacherPhone} placeholder="010-0000-0000" inputProps={{ inputMode: 'numeric' }}
          onChange={(e) => setForm({ ...form, teacherPhone: formatPhoneInput(e.target.value) })} />
        <TextField select label="담당 클럽* (여러 개 가능)" SelectProps={{ multiple: true, renderValue: (v: any) => (v as string[]).join(' · ') }}
          value={form.clubs} onChange={(e) => setForm({ ...form, clubs: e.target.value as unknown as string[] })}>
          {CLUBS.map((c) => <MenuItem key={c.code} value={c.code}><Checkbox size="small" checked={form.clubs.includes(c.code)} />{c.label}</MenuItem>)}
        </TextField>
        <TextField label="직분 (선택)" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>닫기</Button><Button variant="contained" disabled={!valid || busy} onClick={submit}>{busy ? '등록 중…' : '등록'}</Button></DialogActions>
    </Dialog>
  );
};

export default ChurchReportPortal;
